import * as Constants from '../atributoDinamico/constants.js';

/**
 * Lista visible de atributos dinámicos del ABM de Tipos de formulario (VEC-3917).
 *
 * El ABM esconde el atributo marcador de Eric IA (`tipoValor === ID_ERIC_IA`) de la
 * lista arrastrable cuando su config está apagada, y le pasa a cada `Draggable` la
 * posición dentro de ESTA lista filtrada (`react-beautiful-dnd` la necesita
 * consecutiva desde 0 — eso vale mientras nada más la vuelva a filtrar; si además
 * hay atributos inactivos escondidos por `mostrarInactivos`, esa segunda capa de
 * filtro sí puede dejar huecos).
 *
 * El bug: esa misma posición dibujada se usaba también para editar y borrar,
 * pero `handleChangeAtributoDinamico` / `handleDeleteAtributoDinamico` escriben
 * sobre `formData.atributos_dinamicos`, la lista COMPLETA (con Eric adentro). Con
 * Eric escondido, cada tarjeta terminaba operando sobre el atributo ANTERIOR: la
 * posición dibujada es siempre menor o igual a la posición real (Eric ocupa un
 * lugar en la lista completa que no se cuenta en la filtrada), así que usarla tal
 * cual apunta a un atributo que ya pasó, no al siguiente.
 *
 * Esta función resuelve, para cada atributo visible, su posición real en la lista
 * completa (`indiceReal`), para que el ABM edite y borre el atributo correcto sin
 * dejar de dibujar la lista filtrada que necesita el drag and drop.
 */
export function construirListaVisibleAtributos(atributosDinamicos, ericActivo) {
  return (atributosDinamicos || [])
    .map(function (atributoDinamico, indiceReal) {
      return { atributoDinamico: atributoDinamico, indiceReal: indiceReal };
    })
    .filter(function (item) {
      return (
        ericActivo ||
        (item.atributoDinamico && item.atributoDinamico.tipoValor !== Constants.ID_ERIC_IA)
      );
    });
}

/**
 * Reordena la lista COMPLETA de atributos dinámicos a partir de un drag hecho sobre
 * la lista visible (VEC-3917/VEC-3916).
 *
 * `onDragEnd` recibía `source.index`/`destination.index` en términos de la lista
 * filtrada (sin el marcador de Eric, cuando está escondido) y reimplementaba su
 * propia regla de visibilidad para traducirlos a la lista completa. Esta función
 * reusa `construirListaVisibleAtributos` para esa traducción.
 *
 * Con Eric escondido, reinsertarlo por un índice numérico fijo no es equivalente a
 * "dejarlo donde estaba": si el drag mueve atributos de un lado al otro del
 * marcador, ese índice puede terminar apuntando a un lugar distinto de la lista. En
 * cambio, se lo ancla a su vecino anterior (el atributo que tenía justo antes en la
 * lista completa, o el principio de la lista si no tenía ninguno) y se lo reinserta
 * inmediatamente después de donde ese vecino haya quedado tras el drag.
 */
export function reordenarAtributosDinamicos(
  atributosDinamicos,
  ericActivo,
  sourceIndex,
  destinationIndex,
) {
  var listaCompleta = atributosDinamicos || [];
  var visibles = construirListaVisibleAtributos(listaCompleta, ericActivo).map(function (item) {
    return item.atributoDinamico;
  });

  if (visibles.length === listaCompleta.length) {
    // Nada escondido (Eric activo, o no hay marcador de Eric): la lista visible ES
    // la lista completa, no hace falta reinsertar nada.
    var completa = visibles.slice();
    var enMovimiento = completa[sourceIndex];
    completa.splice(sourceIndex, 1);
    completa.splice(destinationIndex, 0, enMovimiento);
    return completa;
  }

  var ericIdx = listaCompleta.findIndex(function (a) {
    return a && a.tipoValor === Constants.ID_ERIC_IA;
  });
  var ericItem = ericIdx >= 0 ? listaCompleta[ericIdx] : null;

  var moviendo = visibles[sourceIndex];
  visibles.splice(sourceIndex, 1);
  visibles.splice(destinationIndex, 0, moviendo);

  if (!ericItem) return visibles;

  var vecinoAnterior = ericIdx > 0 ? listaCompleta[ericIdx - 1] : null;
  var insertAt = vecinoAnterior ? visibles.indexOf(vecinoAnterior) + 1 : 0;
  visibles.splice(insertAt, 0, ericItem);
  return visibles;
}
